"use client";

import { useState } from "react";
import { useTranslations } from "use-intl";

import { cn } from "@/lib/utils";

interface RichTextHtmlProps {
  safeHtml: string;
  maxLines?: number;
  className?: string;
}

function decodeEntities(value: string) {
  return value
    .replace(/&#x([0-9a-f]+);/gi, (match, code) => {
      const point = Number.parseInt(code, 16);
      return Number.isFinite(point) ? String.fromCodePoint(point) : match;
    })
    .replace(/&#(\d+);/g, (match, code) => {
      const point = Number.parseInt(code, 10);
      return Number.isFinite(point) ? String.fromCodePoint(point) : match;
    })
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&");
}

function textLength(html: string) {
  return decodeEntities(html.replace(/<[^>]*>/g, "")).length;
}

export function RichTextHtml({ safeHtml, maxLines = 0, className }: RichTextHtmlProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const t = useTranslations("shared.bioText");
  const needsTruncation = maxLines > 0 && textLength(safeHtml) > maxLines * 100;
  const wrapperClass = cn(
    "text-justify text-muted-foreground whitespace-pre-line hyphens-auto md:text-left [&_a]:text-primary [&_a]:font-medium [&_a]:hover:underline",
    className,
  );
  const htmlProps = { __html: safeHtml };

  if (!needsTruncation || isExpanded) {
    return (
      <div className={wrapperClass}>
        <p dangerouslySetInnerHTML={htmlProps} />
        {needsTruncation && (
          <button
            type="button"
            onClick={() => setIsExpanded(false)}
            className="mt-2 inline-flex min-h-11 items-center rounded-lg px-2 text-sm font-semibold text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {t("showLess")}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <p
        dangerouslySetInnerHTML={htmlProps}
        style={{
          display: "-webkit-box",
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      />
      <button
        type="button"
        onClick={() => setIsExpanded(true)}
        className="mt-2 inline-flex min-h-11 items-center rounded-lg px-2 text-sm font-semibold text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        {t("showMore")}
      </button>
    </div>
  );
}
